/**
 * DashboardBackdrop — the Earth video behind the workspace.
 *
 * Founder, 2026-09-14: "as the background of the dashboard behind the ui
 * elements public/Earth.mp4 display this."
 *
 * WEIGHT. Earth.mp4 is ~1.3 MB (it was 76 MB until it was re-encoded on
 * 2026-09-15 — about the weight of one photo now). That re-encode is what
 * retired most of the caution this file used to carry: there is no longer a
 * width gate, so phones get it too.
 *
 * What remains is what stays right at any size:
 *
 *   - never when the viewer has asked for reduced motion
 *   - never on data-saver, or a 2g connection — save-data is an explicit
 *     request not to spend someone's plan on decoration, and it deserves
 *     honouring whatever the file weighs
 *   - paused while the tab is hidden, so it stops costing battery
 *
 * Anyone failing those checks gets the gradient, which is also what shows while
 * the video is still arriving. The UI never depends on it: this is decoration
 * behind a scrim, never a surface anything is read against.
 */

import React, { useEffect, useRef, useState } from 'react';

const VIDEO_SRC = `${process.env.PUBLIC_URL || ''}/Earth.mp4`;

// Data-saver on, or a connection where even a small file is a poor trade.
// 3g is allowed now — at ~1.3 MB it arrives in a couple of seconds, and the
// gradient covers the wait.
function connectionIsCheap() {
  const c = typeof navigator !== 'undefined' ? navigator.connection : null;
  if (!c) return true; // unknown — the motion gate still applies
  if (c.saveData) return false;
  return !['slow-2g', '2g'].includes(c.effectiveType);
}

export default function DashboardBackdrop() {
  const [playVideo, setPlayVideo] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;

    const still = window.matchMedia('(prefers-reduced-motion: reduce)');

    const decide = () => setPlayVideo(!still.matches && connectionIsCheap());
    decide();

    still.addEventListener('change', decide);

    // Connections change mid-session: someone turns on data-saver, or walks out
    // of wifi. Checking only on mount would keep the video running through
    // exactly the moment the viewer asked us not to.
    const conn = typeof navigator !== 'undefined' ? navigator.connection : null;
    if (conn?.addEventListener) conn.addEventListener('change', decide);

    return () => {
      still.removeEventListener('change', decide);
      if (conn?.removeEventListener) conn.removeEventListener('change', decide);
    };
  }, []);

  // Pause in a hidden tab. One listener, cleaned up — not re-registered on every
  // render, which is what a ref callback would have done.
  useEffect(() => {
    if (!playVideo) return undefined;
    const onVisibility = () => {
      const v = videoRef.current;
      if (!v) return;
      if (document.hidden) v.pause();
      else v.play().catch(() => {});
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [playVideo]);

  return (
    <div className="cfo-backdrop" aria-hidden="true">
      {playVideo && (
        <video
          ref={videoRef}
          className="cfo-backdrop-video"
          src={VIDEO_SRC}
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          disablePictureInPicture
          onCanPlay={(e) => {
            // Autoplay can still be refused (power-saving modes). If it is, the
            // gradient underneath simply stays — nothing breaks.
            const p = e.currentTarget.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
          }}
        />
      )}
      {/* The scrim exists to tame moving imagery — so it only appears with the
          video. Over the bare gradient it would just darken a floor that was
          already designed to stand on its own, which is what most sessions see. */}
      {playVideo && <div className="cfo-backdrop-scrim" />}
    </div>
  );
}
